import React, { useEffect, useState } from 'react'
import { Switch, FormControlLabel } from '@material-ui/core'
import Select from '@material-ui/core/Select'
import { FormattedMessage, useIntl } from 'react-intl'
import InputLabel from '@material-ui/core/InputLabel'
import FormControl from '@material-ui/core/FormControl'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import effects from 'app/effects/effects'
import { addHalfHour, decreaseHalfHour, normalizeTime } from 'libs/dateUtils'

const useStyles = makeStyles((theme) => ({
  timeSelect: {
    width: 130,
  },
  timeRow: {
    display: 'flex',
    alignItems: 'center',
    '@media (max-width: 560px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      position: 'relative',
    },
  },
  timeWeek: {
    width: 74,
    marginRight: 28,
    '@media (max-width: 560px)': {
      marginBottom: '7px',
    },
  },
  timesContainer: {
    width: 300,
  },
  timeSelectFull: {
    width: 'calc(100% - 1.4rem)',
  },
  disabledDay: {
    opacity: 0.7,
  },
  customLabel: {
    marginRight: 6,
    minWidth: 98,
    '& .MuiFormControlLabel-label': {
      color: 'rgb(64 64 64 / 53%) !important',
      fontSize: 13,
    },
    '@media (max-width: 560px)': {
      position: 'absolute',
      top: -11,
      left: 90,
    },
  },
  notProvided: {
    color: 'rgb(64 64 64 / 53%) !important',
    fontSize: 13,
    fontStyle: 'italic',
  },
}))

export default () => {
  const { formatMessage } = useIntl()
  const [loading, setLoading] = useState(true)
  const [openingHours, setOpeningHours] = useState([])

  useEffect(() => {
    effects.apis.openingHours().then((openingHours) => {
      setOpeningHours(openingHours.data)
      setLoading(false)
    })
  }, [])

  const saveOpeningHours = (hours) => {
    return effects.apis.saveOpeningHours(hours)
  }
  const weekDays = [
    formatMessage({ id: 'weekday.monday' }),
    formatMessage({ id: 'weekday.tuesday' }),
    formatMessage({ id: 'weekday.wednesday' }),
    formatMessage({ id: 'weekday.thursday' }),
    formatMessage({ id: 'weekday.friday' }),
    formatMessage({ id: 'weekday.saturday' }),
    formatMessage({ id: 'weekday.sunday' }),
  ]
  const times = [
    '24 hours',
    ...Array(24)
      .fill(1)
      .map((val, i) => {
        return [normalizeTime(i) + ':00', normalizeTime(i) + ':30']
      })
      .flat(),
  ]

  const classes = useStyles()

  const getFromDay = (index, key) => {
    const day = openingHours.find((day) => day.openDay === index)
    if (day) {
      return key ? day[key] : day
    }
    return ''
  }

  const syncWithBackend = (data) => {
    saveOpeningHours(data)
  }

  const changeClosed = (
    isClose,
    index,
    returnNewState = false,
    { value, property } = {},
  ) => {
    let newState
    if (isClose) {
      newState = openingHours.filter((day) => day.openDay !== index)
    } else {
      if (value) {
        newState = [
          ...openingHours,
          {
            closeDay: index,
            closeTime: property === 'openTime' ? addHalfHour(value) : '20:30',
            openDay: index,
            openTime:
              property === 'closeTime' ? decreaseHalfHour(value) : '10:00',
          },
        ]
      } else {
        newState = [
          ...openingHours,
          {
            closeDay: index,
            closeTime: '20:30',
            openDay: index,
            openTime: '10:00',
          },
        ]
      }
    }
    if (!returnNewState) {
      setOpeningHours(newState)
      syncWithBackend(newState)
      return
    }
    return newState
  }

  const notProvided = !openingHours.length

  const changeTimeOfDay = (value, editingIndex, property) => {
    let editableState = openingHours
    const currentDay = editableState.find(
      (day, index) => editingIndex === day.openDay,
    )
    if (!currentDay || notProvided) {
      editableState = changeClosed(false, editingIndex, true, {
        value,
        property,
      })
    }

    const newState = editableState.map((day, index) => {
      if (editingIndex === day.openDay) {
        if (value === '24 hours') {
          return {
            ...day,
            openTime: '24 hours',
            closeTime: '24 hours',
          }
        } else if (property === 'openTime' && value >= day['closeTime']) {
          const newCloseTime = addHalfHour(value)
          return {
            ...day,
            openTime: newCloseTime === '24:00' ? '24 hours' : value,
            closeTime: newCloseTime === '24:00' ? '24 hours' : newCloseTime,
          }
        } else if (property === 'openTime' && day['openTime'] === '24 hours') {
          const newCloseTime = addHalfHour(value)
          return {
            ...day,
            openTime: newCloseTime === '24:00' ? '23:00' : value,
            closeTime: newCloseTime === '24:00' ? '23:30' : newCloseTime,
          }
        } else if (property === 'closeTime' && value < day['openTime']) {
          return {
            ...day,
            openTime: '00:00',
            closeTime: '00:00',
          }
        }
        return {
          ...day,
          [property]: value,
        }
      }
      return day
    })
    setOpeningHours(newState)
    syncWithBackend(newState)
  }

  return (
    <div className="flex flex-col">
      {!loading &&
        weekDays.map((weekDay, index) => (
          <div className={clsx(classes.timeRow, 'mt-24')} key={index}>
            <div className={classes.timeWeek}>{weekDay}</div>
            <FormControlLabel
              control={
                <Switch
                  disableRipple
                  checked={!!getFromDay(index)}
                  onChange={(e) => changeClosed(!e.target.checked, index)}
                  inputProps={{
                    'aria-label': 'Closed',
                    'data-testid': 'closedWeekDay',
                  }}
                />
              }
              label={
                getFromDay(index)
                  ? formatMessage({
                      id: 'label.open',
                    })
                  : formatMessage({
                      id: 'label.closed',
                    })
              }
              className={clsx(classes.customLabel, 'mr-28')}
            />
            <div
              className={clsx(
                classes.timesContainer,
                !getFromDay(index) ? 'invisible' : 'visible',
              )}
            >
              <FormControl
                variant="outlined"
                className={clsx(classes.timeSelect, 'mr-28', {
                  [classes.disabledDay]: notProvided
                    ? false
                    : !getFromDay(index),
                  [classes.timeSelectFull]:
                    getFromDay(index, 'openTime') === '24 hours',
                })}
              >
                <InputLabel
                  className={clsx('MuiFormLabel-filled', 'MuiFormLabel-shrink')}
                  data-shrink={true}
                  htmlFor={`weekFrom${index}`}
                >
                  <FormattedMessage id="profile.info.date.from" />
                </InputLabel>
                <Select
                  native
                  value={getFromDay(index, 'openTime')}
                  onChange={(e) =>
                    changeTimeOfDay(e.target.value, index, 'openTime')
                  }
                  label="From"
                  inputProps={{
                    name: `weekFrom${index}`,
                    id: `weekFrom${index}`,
                  }}
                >
                  {!getFromDay(index) && <option value="" selected />}
                  {times.slice(0, -1).map((item, index) => {
                    return (
                      <option key={index} value={item}>
                        {item}
                      </option>
                    )
                  })}
                </Select>
              </FormControl>
              {getFromDay(index, 'openTime') !== '24 hours' && (
                <FormControl
                  variant="outlined"
                  className={clsx(classes.timeSelect, {
                    [classes.disabledDay]: notProvided
                      ? false
                      : !getFromDay(index),
                  })}
                >
                  <InputLabel id="demo-simple-select-outlined-label">
                    <FormattedMessage id="profile.info.date.to" />
                  </InputLabel>
                  <Select
                    native
                    value={getFromDay(index, 'closeTime')}
                    onChange={(e) =>
                      changeTimeOfDay(e.target.value, index, 'closeTime')
                    }
                    label="To"
                  >
                    {!getFromDay(index) && <option value="" selected />}
                    {times
                      .filter(
                        (time, i) =>
                          time > getFromDay(index, 'openTime') && i !== 1,
                      )
                      .map((item, index) => {
                        return (
                          <option key={index} value={item}>
                            {item}
                          </option>
                        )
                      })}
                  </Select>
                </FormControl>
              )}
            </div>
          </div>
        ))}
    </div>
  )
}
